/* eslint-disable no-unused-vars */
/**
 * @name AuthUtils
 *
 * @version 0.0.1
 *
 * @copyright 2024 TorkHub
 * @description Utility functions for authentication and token management
 */
import { supabase } from '@/supabase'
import axios from 'axios'

export default new class {
  constructor() {
    // Cache key for localStorage
    this.CACHE_KEY = 'torkhub_auth_cache'
    
    // Buffer time before expiration to refresh token (5 minutes)
    this.REFRESH_BUFFER_MS = 5 * 60 * 1000
    
    // Add integrity checking
    this.CACHE_VERSION = '1.0'
  }

  /**
   * Simple obfuscation (NOT ENCRYPTION - just makes tokens less obvious in dev tools)
   * @private
   */
  obfuscateData(data) {
    try {
      const jsonStr = JSON.stringify(data)
      return btoa(jsonStr) // Base64 encode
    } catch (error) {
      console.error('Error obfuscating data:', error)
      return null
    }
  }

  /**
   * Deobfuscate data
   * @private
   */
  deobfuscateData(obfuscatedData) {
    try {
      const jsonStr = atob(obfuscatedData) // Base64 decode
      return JSON.parse(jsonStr)
    } catch (error) {
      console.error('Error deobfuscating data:', error)
      return null
    }
  }

  /**
   * Get token cache from localStorage with security checks
   * @private
   */
  getTokenCache() {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY)
      if (!cached) {
        return this.getEmptyCache()
      }

      const deobfuscated = this.deobfuscateData(cached)
      if (!deobfuscated || !this.validateCacheIntegrity(deobfuscated)) {
        console.warn('Invalid cache detected, clearing...')
        this.clearTokenCache()
        return this.getEmptyCache()
      }

      return deobfuscated.data
    } catch (error) {
      console.error('Error reading token cache:', error)
      this.clearTokenCache()
      return this.getEmptyCache()
    }
  }

  /**
   * Get empty cache structure
   * @private
   */
  getEmptyCache() {
    return {
      hasuraJwt: null,
      expiresAt: null,
      supabaseToken: null,
      supabaseExpiresAt: null
    }
  }

  /**
   * Validate cache integrity
   * @private
   */
  validateCacheIntegrity(cache) {
    return cache && 
           typeof cache === 'object' && 
           cache.version === this.CACHE_VERSION &&
           cache.data &&
           typeof cache.data === 'object'
  }

  /**
   * Save token cache to localStorage with security measures
   * @private
   */
  setTokenCache(cache) {
    try {
      // Add metadata and version
      const cacheWithMetadata = {
        version: this.CACHE_VERSION,
        timestamp: Date.now(),
        data: cache
      }

      const obfuscated = this.obfuscateData(cacheWithMetadata)
      if (obfuscated) {
        localStorage.setItem(this.CACHE_KEY, obfuscated)
      }
    } catch (error) {
      console.error('Error saving token cache:', error)
      // If we can't save securely, don't save at all
      this.clearTokenCache()
    }
  }

  /**
   * Get the current Supabase JWT access token (actually returns Hasura JWT)
   * Note: Method name kept for backward compatibility - this returns a Hasura JWT token
   * @returns {Promise<string|null>} The Hasura JWT token or null if no session
   */
  async getSupabaseToken () {
    // Check if we have a valid cached token
    const tokenCache = this.getTokenCache()
    if (tokenCache.hasuraJwt && tokenCache.expiresAt) {
      const now = Date.now()
      const timeUntilExpiry = tokenCache.expiresAt - now
      
      // If token is still valid (with buffer), return cached version
      if (timeUntilExpiry > this.REFRESH_BUFFER_MS) {
        return tokenCache.hasuraJwt
      }
    }

    // Need to fetch new token
    return await this.refreshHasuraToken()
  }

  /**
   * Get the raw Supabase access token (not the Hasura JWT)
   * @returns {Promise<string|null>} The raw Supabase access token or null if no session
   */
  async getRawSupabaseToken () {
    // Check if we have a valid cached Supabase token
    const tokenCache = this.getTokenCache()
    if (tokenCache.supabaseToken && tokenCache.supabaseExpiresAt) {
      const now = Date.now()
      const timeUntilExpiry = tokenCache.supabaseExpiresAt - now
      
      // If token is still valid (with buffer), return cached version
      if (timeUntilExpiry > this.REFRESH_BUFFER_MS) {
        return tokenCache.supabaseToken
      }
    }

    // Need to fetch new Supabase token
    return await this.refreshSupabaseToken()
  }

  /**
   * Refresh the Hasura JWT token
   * @private
   */
  async refreshHasuraToken () {
    try {
      const supabaseToken = await this.getRawSupabaseToken()
      if (!supabaseToken) {
        this.clearTokenCache()
        return null
      }

      const jwtResponse = await axios({
        url: 'https://token.do.torkhub.app/tokens',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apiKey: process.env.VUE_APP_SUPABASE_ANON_KEY
        },
        data: {
          supabase_access_token: supabaseToken,
          ttl: 3600, // 1 hour for better security
          allowed_roles: ['admin'],
          default_role: 'admin'
        }
      })

      // Cache the new token
      const tokenCache = this.getTokenCache()
      tokenCache.hasuraJwt = jwtResponse.data.hasura_jwt_token
      // Set expiration to TTL minus buffer for safety
      tokenCache.expiresAt = Date.now() + (3600 * 1000) - this.REFRESH_BUFFER_MS
      this.setTokenCache(tokenCache)

      return tokenCache.hasuraJwt
    } catch (error) {
      console.error('Error refreshing Hasura token:', error)
      this.clearTokenCache()
      return null
    }
  }

  /**
   * Refresh the Supabase token
   * @private
   */
  async refreshSupabaseToken () {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const supabaseToken = session?.access_token || null
      
      if (supabaseToken) {
        // Cache the Supabase token
        const tokenCache = this.getTokenCache()
        tokenCache.supabaseToken = supabaseToken
        // Supabase tokens typically expire in 1 hour
        tokenCache.supabaseExpiresAt = Date.now() + (60 * 60 * 1000) - this.REFRESH_BUFFER_MS
        this.setTokenCache(tokenCache)
      }
      
      return supabaseToken
    } catch (error) {
      console.error('Error refreshing Supabase token:', error)
      this.clearTokenCache()
      return null
    }
  }

  /**
   * Get the current Supabase session
   * @returns {Promise<object|null>} The full session object or null if no session
   */
  async getSupabaseSession () {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      return session
    } catch (error) {
      console.error('Error getting Supabase session:', error)
      return null
    }
  }

  /**
   * Check if user is authenticated with Supabase
   * @returns {Promise<boolean>} True if user has a valid session
   */
  async isAuthenticated () {
    try {
      const session = await this.getSupabaseSession()
      return !!session
    } catch (error) {
      console.error('Error checking authentication:', error)
      return false
    }
  }

  /**
   * Clear all cached tokens (useful for logout)
   */
  clearTokenCache () {
    try {
      localStorage.removeItem(this.CACHE_KEY)
    } catch (error) {
      console.error('Error clearing token cache:', error)
    }
  }

  /**
   * Force refresh of all tokens (bypasses cache)
   */
  async forceRefreshTokens () {
    this.clearTokenCache()
    return await this.getSupabaseToken()
  }

  /**
   * Get cache info for debugging (without exposing tokens)
   */
  getCacheInfo () {
    const cache = this.getTokenCache()
    return {
      hasToken: !!cache.hasuraJwt,
      expiresAt: cache.expiresAt ? new Date(cache.expiresAt).toISOString() : null,
      hasSupabaseToken: !!cache.supabaseToken,
      supabaseExpiresAt: cache.supabaseExpiresAt ? new Date(cache.supabaseExpiresAt).toISOString() : null
    }
  }
}() 